import client from './index'

import {
  ApiPaginationResponse,
  PaginationParams,
  ApiResponse,
} from '@/models/api'
import {
  User,
  UserStatus,
  UserStatusEv,
  UpdateableUserParams,
  UserWithCarsProfiles,
  UserInsurancePolicy,
  UpdatableUserInsurancePolicyParams,
} from '@/models/user'
import { PapersVerifyingPayload } from '@/store/users'

export const getUsers = async (params: PaginationParams) => {
  return client.get<ApiPaginationResponse<User>>('/users', { params })
}

export const getEvUsers = async (params: PaginationParams & { q?: string }) => {
  return client.get<ApiPaginationResponse<User>>('/users/ev', { params })
}

export const getAllUsers = () => {
  return client.get<User[]>('/users/all')
}

export const checkStatusUsers = () => {
  return client.get<User[]>('/users/check-status')
}

export const pushOneNotification = (notificationData: any) => {
  return client.post<ApiResponse<void>>(
    '/push-notifications/user',
    notificationData
  )
}

export const pushManyNotification = (notificationData: any) => {
  return client.post<ApiResponse<void>>(
    '/push-notifications/users',
    notificationData
  )
}

export const getUserDetails = (id: string) => {
  return client.get<ApiResponse<UserWithCarsProfiles>>(`/users/${id}`)
}

export interface UserDocumentSnapshot {
  id: number
  user_id: string
  action: string
  documents: Partial<User>
  created_at: string
  updated_at: string
}

export const getUserDocumentSnapshots = (id: string) => {
  return client.get<ApiResponse<UserDocumentSnapshot[]>>(
    `/users/${id}/document-snapshots`
  )
}

export const restoreUserDocumentsFromSnapshot = (
  id: string,
  snapshotId: number
) => {
  return client.post<ApiResponse<User>>(
    `/users/${id}/document-snapshots/${snapshotId}/restore`
  )
}

export const updateUserStatus = (
  id: string,
  status: UserStatus,
  reason?: string
) => {
  return client.put<ApiResponse<{ status: UserStatus }>>(
    `/users/${id}/update-status`,
    {
      status,
      ...(reason ? { reason } : {}),
    }
  )
}

export const updateUserStatusEV = (id: string, status_ev: UserStatusEv) => {
  return client.put<ApiResponse<{ status: UserStatusEv }>>(
    `/users/${id}/update-status-ev`,
    { status_ev }
  )
}

export const bulkCheckEvEligibility = () => {
  return client.post<
    ApiResponse<{ checkedCount: number; activatedCount: number }>
  >(
    '/users/bulk-check-ev',
    {},
    { timeout: 300000 } // Set timeout to 5 minutes to prevent 10s cutoff
  )
}

export const verifyPaper = (payload: PapersVerifyingPayload) => {
  return client.put(`/users/${payload.id}/verify-papers`, {
    name: payload.paper,
    verified: payload.value,
  })
}

export const resetPassword = (userId: string) =>
  client.get<ApiResponse<void>>(`/users/${userId}/reset-password`)

export const updateUser = (id: string, params: UpdateableUserParams) => {
  return client.put<ApiResponse<User>>(`/users/${id}`, params)
}

export const getUserInsuranceDetails = (id: string) => {
  return client.get<ApiResponse<UserInsurancePolicy>>(
    `/users/${id}/insurance-information`
  )
}
// export const getUserInsuranceDetails = async (id: string) => {
//   const response = await client.get<ApiResponse<UserInsurancePolicy>>(
//     `/users/${id}/insurance-information`
//   )
//   response);
//   return response;
// }

export const updateUserInsuranceDetails = (
  id: string,
  params: Partial<UpdatableUserInsurancePolicyParams>
) => {
  return client.put<ApiResponse<UserInsurancePolicy>>(
    `/users/${id}/insurance-information`,
    params
  )
}

export const deleteUserPaymentCard = ({
  userId,
  cardId,
}: {
  userId: string
  cardId: string
}) => {
  // "🚀 ~ cardId:", cardId)
  // "🚀 ~ userId:", userId)
  return client.delete<ApiResponse<void>>(
    `/users/${userId}/paymentsCard/${cardId}`
  )
}

export const createUserInsuranceDetails = (
  id: string,
  params: UpdatableUserInsurancePolicyParams
) => {
  return client.post<ApiResponse<UserInsurancePolicy>>(
    `/users/${id}/insurance-information`,
    params
  )
}

export const cleanExpiredDriverLicenses = () => {
  return client.post('/users/expired-driver-licenses')
}

// New API functions for optimized notifications
export const getUsersStats = () => {
  return client.get('/users/stats')
}

export const searchUsers = (
  query: string,
  type: 'name' | 'id' | 'phone' = 'name'
) => {
  return client.get(
    `/users/search?query=${encodeURIComponent(query)}&type=${type}`
  )
}

export const downloadUserDocument = (userId: string, documentType: string) => {
  return client.post(
    `/users/${userId}/download-user-document`,
    { document_type: documentType },
    { responseType: 'blob' }
  )
}
